import {isStripeVerificationValueCorrect} from "@buildwithflux/core";
import {FunctionsAdapter} from "@buildwithflux/firebase-functions-adapter";
import {
    AmountInDollars,
    getCurrentOnboardingStep,
    getCurrentOnboardingStepForUser,
    IUserData,
    IUserPrivateMetadata,
    OnboardingFlow,
    OnboardingStep,
    PaymentPlanCategory,
    PlanEpoch,
    PlanVariantForEpochFrom202410,
    SuccessfulStripeVerificationUrlParam,
    SubscriptionStatus,
    TrialType,
    UserUid,
    getPaymentPlanCategory,
    isPaidPaymentPlanCategory,
    SurfaceBasedTrackingEvents,
} from "@buildwithflux/models";
import {
    getPlanEpochFromUserPrivateMetadata,
    getPlanVariantFromUserPrivateMetadata,
    getTrialTypeFromUserPrivateMetadata,
} from "@buildwithflux/plans";
import {UserPrivateMetadataRepository, UserRepository, OrganizationRepository} from "@buildwithflux/repositories";
import {areWeTestingWithJest, isDevEnv, Logger} from "@buildwithflux/shared";
import {create as createMutative, type Draft} from "mutative";
import {noop} from "lodash";
import {create} from "zustand";
import {devtools} from "zustand/middleware";

import {ChosenUserPlan} from "components/common/components/dialog/ChoosePlanDialogContent";
import {useFluxServices} from "injection/hooks";
import {CurrentUserService} from "modules/auth";
import {FeatureFlagsState, FeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";
import {AnalyticsStorage} from "modules/storage_engine/AnalyticsStorage";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {
    assertOnboardingDialogStateInitialized,
    getSearchParamsFromLocation,
    onboardingDialogStateIsAtStep,
    removeAllOnboardingSearchParamsFromLocation,
} from "./helpers";
import {SurveyData, getCurrentSurveyData, surveyDataIsComplete} from "./survey";
import {
    ChooseInvitesState,
    ChoosePlanState,
    ChooseInvitesStepCallbackArgs,
    OnboardingStoreState,
    SurveyState,
    UninitializedState,
    UseOnboardingDialogStore,
} from "./types";

/**
 * Service dependencies for creating the store
 */
type Services = {
    currentUserService: CurrentUserService;
    userRepository: UserRepository;
    userPrivateMetadataRepository: UserPrivateMetadataRepository;
    organizationRepository: OrganizationRepository;
    analyticsStorage: AnalyticsStorage;
    functionsAdapter: FunctionsAdapter;
    featureFlagsStore: FeatureFlagsStore;
    logger: Logger;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const onUserChanged = async (_user: IUserData, _privateMetadata: IUserPrivateMetadata) => {}; // noop for most steps

/**
 * The initial uninitialized state of the store
 */
const uninitialized: UninitializedState = {
    step: "uninitialized",
    userUid: undefined,
    userHandle: undefined,
    flow: undefined,
    completedSteps: [],
    isShown: false,
    allowsDismissal: false,
    onUserChanged: undefined,
    onDismiss: undefined,
    callbacks: {},
};

// Step functions themselves don't change these in-band
type NonStepStateKeys = "userUid" | "userHandle" | "completedSteps" | "flow";

export type StateProducer = (draftState: Draft<OnboardingStoreState>) => void;
export type Set = (
    nextStateProducer: StateProducer | OnboardingStoreState,
    shouldReplace: boolean,
    actionNameForDebugging: string,
) => void;
export type Get = () => OnboardingStoreState;
export type StepFunction = (set: Set, get: Get, services: Services) => Promise<void>;

/**
 * Check if the current user has organization-provided entitlements
 * Replicates the logic from useIsCurrentUserMemberOfOrganizationWithPaidPlan
 */
async function checkUserHasOrganizationProvidedPlan(services: Services): Promise<boolean> {
    try {
        const organizationMemberships =
            await services.organizationRepository.getAllDecoratedOrganizationMembersForCurrentUser(
                services.currentUserService,
            );

        for (const membership of organizationMemberships) {
            const planCategory = getPaymentPlanCategory(membership.organization);
            if (isPaidPaymentPlanCategory(planCategory)) {
                return true;
            }
        }

        return false;
    } catch (error) {
        services.logger.error("Error checking organization-provided plan", error);
        return false;
    }
}

function getStateUpdater(
    state: Omit<OnboardingStoreState, NonStepStateKeys> & Partial<Pick<OnboardingStoreState, NonStepStateKeys>>,
): StateProducer {
    return (draftState) => {
        draftState.step = state.step;
        draftState.isShown = state.isShown;
        draftState.allowsDismissal = state.allowsDismissal;
        draftState.onUserChanged = state.onUserChanged;
        draftState.onDismiss = state.onDismiss;
        draftState.callbacks = state.callbacks;

        for (const step of state.completedSteps ?? []) {
            if (!draftState.completedSteps.includes(step)) {
                draftState.completedSteps.push(step);
            }
        }
    };
}

async function markStepCompleted(
    set: Set,
    get: Get,
    services: Services,
    step: OnboardingStep,
    reason: string,
): Promise<void> {
    const state = get();
    assertOnboardingDialogStateInitialized(state);

    set(
        (draft) => {
            if (!draft.completedSteps.includes(step)) {
                draft.completedSteps.push(step);
            }
        },
        false,
        `markStepCompleted(${step}, reason: ${reason})`,
    );

    await services.userPrivateMetadataRepository.markOnboardingStepsComplete(state.userUid, [step]);
}

function getNextStep(get: Get, fromCurrentStep: OnboardingStep): OnboardingStep {
    const state = get();
    const {flow, completedSteps} = state;

    const stepsWithCurrentCompleted = new Set<OnboardingStep>(completedSteps);
    stepsWithCurrentCompleted.add(fromCurrentStep);

    return getCurrentOnboardingStep(flow ?? OnboardingFlow.enum.standard, stepsWithCurrentCompleted);
}

/**
 * Moves to the next step
 */
async function moveToNextStep(
    set: Set,
    get: Get,
    services: Services,
    fromStep: OnboardingStep,
    reason: string,
): Promise<void> {
    const toStep = getNextStep(get, fromStep);

    const state = get();
    assertOnboardingDialogStateInitialized(state);

    // In parallel, start loading the new state, and mark the current step complete
    await Promise.all([
        await markStepCompleted(set, get, services, fromStep, reason),
        await getStepFunction(toStep)(set, get, services),
    ]);
}

/**
 * Gets the step function for a given step
 */
function getStepFunction(step: OnboardingStep): StepFunction {
    switch (step) {
        case OnboardingStep.enum.survey:
            return surveyStep;
        case OnboardingStep.enum.chooseInvites:
            return chooseInvitesStep;
        case OnboardingStep.enum.choosePlan:
            return choosePlanStep;
        case OnboardingStep.enum.done:
            return doneStep;
        default:
            throw new Error(`Unknown step: ${step}`);
    }
}

/**
 * As we start up the store, we subscribe to the current user service to receive changes in the current
 * user and user private metadata. We mainly do this so that we can reset the state if the user logs out
 * and start the initial state if the user logs in.
 *
 * We don't closely track changes to the user nor their privateMetadata: instead, steps that need that
 * information can retrieve it from services.currentUserService.getCurrentUser() - doing it this way
 * prevents duplicate copies of the user data being stored on the store, which could otherwise go out
 * of sync.
 *
 * The one exception is that we notify the current step function when the user changes, so that it can
 * react to the change if necessary. This should _not_ react to onboarding data itself (which is stored
 * internally on this store as the source-of-truth)
 */
async function updateUser(
    set: Set,
    get: Get,
    services: Services,
    currentUserInfo: {
        user: IUserData | undefined;
        privateMetadata: IUserPrivateMetadata | undefined;
    },
): Promise<void> {
    let state = get() ?? uninitialized;
    const user = currentUserInfo.user;

    // If a new user has logged in, or the current user has logged out, the state needs to go back to uninitialized
    if (user?.uid !== state?.userUid || user?.handle !== state?.userHandle) {
        state = uninitialized;
        set(state, false, "userChanged");
    }

    // And if there's no current user, that's all we need to do
    if (!user || user.isAnonymous) {
        set(state, false, "noUser");
        return;
    }

    // Fake out the privateMetadata if it's completely missing (it's probably still loading)
    let privateMetadata = currentUserInfo.privateMetadata ?? {uid: user.uid};

    if (state.step === "uninitialized") {
        // We do not proceed any further if we are already marked onboarded
        if (privateMetadata?.onboarding?.onboarded === true) {
            return;
        }

        const hasOrgProvidedPlan = await checkUserHasOrganizationProvidedPlan(services);
        if (hasOrgProvidedPlan) {
            await services.userPrivateMetadataRepository.update(user.uid, {
                onboarding: {
                    onboarded: true,
                },
            });
            await doneStep(set, get, services);
            return;
        }

        // Check if user needs migration to latest onboarding state
        // Only call the migration function for users who would see legacy NUX
        const currentPlanEpoch = getPlanEpochFromUserPrivateMetadata(privateMetadata);
        const currentPlanVariant = getPlanVariantFromUserPrivateMetadata(privateMetadata);
        const currentTrialType = getTrialTypeFromUserPrivateMetadata(privateMetadata);

        const needsMigration =
            currentPlanEpoch !== PlanEpoch.enum.from_202410 ||
            currentPlanVariant !== PlanVariantForEpochFrom202410.enum.from_202504 ||
            currentTrialType !== TrialType.enum.payment_details_up_front;

        if (needsMigration) {
            // Migrate user to latest onboarding state before showing NUX
            // This ensures users with outdated plan epoch, plan variant, or trial type
            // are updated to the latest values, allowing us to remove legacy code paths
            try {
                const migrationResult = await services.functionsAdapter.migrateUserToLatestOnboardingState();
                if (migrationResult.migrated) {
                    services.logger.info("User migrated to latest onboarding state", {
                        previousState: migrationResult.previousState,
                    });
                    // Refetch metadata to ensure we have the latest values after migration
                    privateMetadata =
                        (await services.userPrivateMetadataRepository.getForUser(user.uid)) ?? privateMetadata;
                }
            } catch (error) {
                // Log but don't block onboarding if migration fails
                services.logger.error("Failed to migrate user to latest onboarding state", error);
            }
        }

        // Now, if we are currently uninitialized, we need to find and set up the initial state of the dialog
        const flow = getOnboardingFlowForUser({privateMetadata, featureFlags: get().featureFlags});
        services.logger.debug(`OnboardingStore: got initial onboarding flow for user: ${flow}`, {
            flow,
            featureFlags: state.featureFlags,
        });

        set(
            (draft) => {
                draft.userUid = user.uid;
                draft.userHandle = user.handle;
                draft.completedSteps = privateMetadata.onboarding?.completedSteps ?? [];
                draft.flow = flow;
            },
            false,
            "initializing",
        );

        // We should persist the flow we're going to use, so we can always use the same one later
        if (!privateMetadata.onboarding?.flow) {
            void services.userPrivateMetadataRepository.update(user.uid, {
                onboarding: {
                    flow,
                },
            });
        }

        const nextStep = getCurrentOnboardingStepForUser(flow, privateMetadata);

        if (nextStep === OnboardingStep.enum.survey) {
            await surveyStep(set, get, services);
        } else if (nextStep === OnboardingStep.enum.chooseInvites) {
            await chooseInvitesStep(set, get, services);
        } else if (nextStep === OnboardingStep.enum.choosePlan) {
            await choosePlanStep(set, get, services);
        } else if (nextStep === OnboardingStep.enum.done) {
            await doneStep(set, get, services);
        } else {
            throw new Error("Unknown step: " + nextStep);
        }
    } else {
        // If the user has been marked as onboarded since we last checked (e.g. by acceptProjectInvite
        // running after the store was already initialized), skip straight to done
        if (privateMetadata?.onboarding?.onboarded === true && state.step !== OnboardingStep.enum.done) {
            await doneStep(set, get, services);
            return;
        }

        // Similarly, if the user now belongs to a paid organization, mark them onboarded and skip to done
        if (state.step !== OnboardingStep.enum.done) {
            const hasOrgProvidedPlan = await checkUserHasOrganizationProvidedPlan(services);
            if (hasOrgProvidedPlan) {
                await services.userPrivateMetadataRepository.update(user.uid, {
                    onboarding: {
                        onboarded: true,
                    },
                });
                await doneStep(set, get, services);
                return;
            }
        }

        // And if we are already initialized, we need to let the current step function know about the possible update
        state.onUserChanged ? await state.onUserChanged(user, privateMetadata) : void 0;
    }
}

function updateFeatureFlags(set: Set, _get: Get, _service: Services, _state: FeatureFlagsState): void {
    set(
        (draft) => {
            if (!draft.featureFlags) {
                draft.featureFlags = {};
            }
            // segmentOnboardingFlowStreamlinedExperiment is now always off
        },
        false,
        "updateFeatureFlags",
    );
}

export function getOnboardingFlowForUser({
    privateMetadata,
}: {
    privateMetadata: IUserPrivateMetadata;
    featureFlags?: unknown;
}): OnboardingFlow {
    if (privateMetadata.onboarding?.flow) {
        return privateMetadata.onboarding.flow;
    }

    // segmentOnboardingFlowStreamlinedExperiment is now always off
    // Always use the standard onboarding flow
    return OnboardingFlow.enum.standard;
}

/**
 * If there is an ?sv={hash} search param, indicating a successful Stripe checkout, we continue to
 * the next step.
 *
 * This is the main means by which we move to the next step for paid plans and trials
 *
 * A boolean is returned so callers can optionally skip their own changes to the state
 */
async function checkForSuccessfulPayment(set: Set, get: Get, services: Services, userUid: UserUid): Promise<boolean> {
    const searchParams = getSearchParamsFromLocation();

    if (
        searchParams.has(SuccessfulStripeVerificationUrlParam) &&
        isStripeVerificationValueCorrect(userUid, searchParams.get(SuccessfulStripeVerificationUrlParam) ?? "")
    ) {
        void services.analyticsStorage.logEvent(SurfaceBasedTrackingEvents.onboardingUx, {
            step: "stripeResult",
            action: "1", // Backward-compatible, but weird (we'd set this to "1" because it came from the stripeSuccess URL param we set on the successUrl)
            content_type: "onboardingDialog",
        });

        await moveToNextStep(set, get, services, OnboardingStep.enum.choosePlan, "surveyCompleteElsewhere");
        return true;
    }

    return false;
}

/**
 * A secondary means by which we move to the next step: if the user has completed a checkout in a
 * different tab or window, and we have processed the Stripe webhook in the backend
 *
 * A boolean is returned so callers can skip their own changes to the state
 */
async function checkForActiveSubscription(
    set: Set,
    get: Get,
    services: Services,
    privateMetadata: IUserPrivateMetadata,
): Promise<boolean> {
    if (
        Object.values(privateMetadata?.payment?.subscriptions || {}).filter(
            (subscription) =>
                subscription.status === SubscriptionStatus.enum.active ||
                subscription.status === SubscriptionStatus.enum.trialing ||
                subscription.status === SubscriptionStatus.enum.incomplete,
        ).length > 0
    ) {
        await moveToNextStep(
            set,
            get,
            services,
            OnboardingStep.enum.choosePlan,
            "onUserChanged(nowHasActiveSubscription)",
        );
        return true;
    }

    return false;
}

// Step functions

async function surveyStep(set: Set, get: Get, services: Services): Promise<void> {
    services.logger.debug("Starting surveyStep");

    const state = get();
    assertOnboardingDialogStateInitialized(state);
    const {userHandle, userUid} = state;

    // If survey is already complete when starting the survey step, move directly to the next step
    if (
        surveyDataIsComplete(
            getCurrentSurveyData({
                user: services.currentUserService.getCurrentUser(),
                privateMetadata: services.currentUserService.getCurrentUserPrivateMetadata(),
            }),
        )
    ) {
        await moveToNextStep(set, get, services, OnboardingStep.enum.survey, "surveyAlreadyComplete");
        return;
    }

    const onUpdateSurvey = async (surveyData: SurveyData) => {
        await Promise.all([
            services.userRepository.update(userHandle, {
                full_name: surveyData.fullName,
            }),
            services.userPrivateMetadataRepository.update(userUid, {
                uid: userUid,
                personal: {
                    role: surveyData.role || null,
                    company: surveyData.company || null,
                    experience: surveyData.experience || null,
                    hearAboutFlux: surveyData.hearAboutFlux || null,
                },
            }),
        ]);
    };

    set(
        getStateUpdater({
            step: OnboardingStep.enum.survey,
            isShown: true,
            allowsDismissal: false,
            callbacks: {
                onUpdateSurvey,

                async onCompleteSurvey(surveyData: SurveyData) {
                    await onUpdateSurvey(surveyData);
                    await moveToNextStep(set, get, services, OnboardingStep.enum.survey, "onCompleteSurvey");
                },
            },
            onUserChanged,
        }),
        false,
        "surveyStep",
    );
}

/**
 * Helper function to check if a user is educational based on their privateMetadata
 * This is a non-hook version for use in store functions
 */
function isUserEducational(privateMetadata: IUserPrivateMetadata | undefined): boolean {
    if (!privateMetadata) {
        return false;
    }
    return !!(
        privateMetadata.flags?.account?.educationalQualified ||
        privateMetadata.plans?.isEducational ||
        privateMetadata.creationFlags?.isEducational
    );
}

async function chooseInvitesStep(set: Set, get: Get, services: Services): Promise<void> {
    services.logger.debug("Starting chooseInvitesStep");

    const state = get();
    assertOnboardingDialogStateInitialized(state);
    const {userUid} = state;

    // Educational users skip the invite step - they go directly to plan selection
    const privateMetadata = services.currentUserService.getCurrentUserPrivateMetadata();
    if (isUserEducational(privateMetadata)) {
        services.logger.debug("Educational user detected, skipping chooseInvites step");
        await moveToNextStep(set, get, services, OnboardingStep.enum.chooseInvites, "skipForEducationalUser");
        return;
    }

    async function onCompleteInvites(invitesData: ChooseInvitesStepCallbackArgs): Promise<void> {
        const {invitedEmails} = invitesData;

        if (invitedEmails.length > 0) {
            await services.userPrivateMetadataRepository.update(userUid, {
                onboarding: {
                    invitedEmails,
                },
            });
        }

        await moveToNextStep(set, get, services, OnboardingStep.enum.chooseInvites, "onCompleteInvites");
        return;
    }

    set(
        getStateUpdater({
            step: OnboardingStep.enum.chooseInvites,
            isShown: true,
            allowsDismissal: false,
            callbacks: {
                onCompleteInvites,
            },
            onUserChanged,
        }),
        false,
        "chooseInvitesStep",
    );
}

async function choosePlanStep(set: Set, get: Get, services: Services): Promise<void> {
    services.logger.debug("Starting choosePlanStep");

    const state = get();
    assertOnboardingDialogStateInitialized(state);

    const completedFromSuccessfulPayment = await checkForSuccessfulPayment(set, get, services, state.userUid);

    if (completedFromSuccessfulPayment) {
        return;
    }

    const privateMetadata = services.currentUserService.getCurrentUserPrivateMetadata();

    if (privateMetadata) {
        const completedFromActiveSubscription = await checkForActiveSubscription(set, get, services, privateMetadata);

        if (completedFromActiveSubscription) {
            return;
        }
    }

    const onPlanChosen = async (planChosen: ChosenUserPlan, price?: AmountInDollars) => {
        void services.analyticsStorage.trackChoosePlan(planChosen, price);

        if (planChosen === PaymentPlanCategory.enum.userCore) {
            // Educational users selecting the Core plan with 100% discount
            // The createEducationalPlanCheckout function handles the subscription creation
            await moveToNextStep(set, get, services, OnboardingStep.enum.choosePlan, "onPlanChosen(educationalCore)");
        } else if (
            planChosen === PaymentPlanCategory.enum.userLegacyFree ||
            planChosen === PaymentPlanCategory.enum.userNoPlan
        ) {
            /*
             * Don't call this for other plans, because the user hasn't completed the plan step
             * until they've paid in Stripe for those. At which point, we'll re-enter this step,
             * and the above logic will re-run, detect Stripe success and continue on to the next
             * step.
             */
            await moveToNextStep(set, get, services, OnboardingStep.enum.choosePlan, "onPlanChosen(freeOrNoPlan)");
        }
    };

    set(
        getStateUpdater({
            step: OnboardingStep.enum.choosePlan,
            isShown: true,
            allowsDismissal: false,
            callbacks: {onPlanChosen},

            async onUserChanged(_user: IUserData, privateMetadata: IUserPrivateMetadata) {
                await checkForActiveSubscription(set, get, services, privateMetadata);
            },
        }),
        false,
        "choosePlanStep",
    );
}

async function doneStep(set: Set, get: Get, services: Services): Promise<void> {
    services.logger.debug("Starting doneStep");

    const state = get();
    assertOnboardingDialogStateInitialized(state);

    const {userUid} = state;

    await Promise.all([
        services.userPrivateMetadataRepository.update(userUid, {onboarding: {onboarded: true}}),
        markStepCompleted(set, get, services, OnboardingStep.enum.done, "doneStep"),
    ]);

    removeAllOnboardingSearchParamsFromLocation();

    set(
        getStateUpdater({
            step: OnboardingStep.enum.done,
            isShown: false,
            allowsDismissal: false,
            onUserChanged: undefined,
            callbacks: {},
        }),
        false,
        "doneStep",
    );
}

// End of Step functions

/**
 * The onboarding store holds the state for the OnboardingDialog, including which "step" the user is on, and how to
 * move between steps
 */
export function createOnboardingDialogStore(services: Services): UseOnboardingDialogStore {
    return create<OnboardingStoreState>()(
        devtools(
            (setZustand, get): OnboardingStoreState => {
                const set: Set = (nextStateProducer, shouldReplace, actionNameForDebugging) => {
                    if (typeof nextStateProducer === "function") {
                        setZustand(createMutative(nextStateProducer), shouldReplace, actionNameForDebugging);
                    } else {
                        setZustand(nextStateProducer, shouldReplace, actionNameForDebugging);
                    }
                };

                // Set up the user and feature subscriber
                setTimeout(() => {
                    const featureFlagUnsubscriber = services.featureFlagsStore.subscribe(
                        (state) => void updateFeatureFlags(set, get, services, state),
                    );

                    // And call once with the current value, in case the subscription doesn't emit a value
                    void updateFeatureFlags(set, get, services, services.featureFlagsStore.getState());

                    const userChangeUnsubscriber = services.currentUserService.subscribeToUserChanges(
                        (currentUserInfo) => {
                            void updateUser(set, get, services, currentUserInfo).catch((err) => {
                                services.logger.error("Error while responding to user change", err);
                                FluxLogger.captureError(err);
                            });
                        },
                    );

                    // And call once with the current value, in case the subscription doesn't emit a value
                    void updateUser(set, get, services, {
                        user: services.currentUserService.getCurrentUser(),
                        privateMetadata: services.currentUserService.getCurrentUserPrivateMetadata(),
                    });

                    // Store the subscriber for later cleanup
                    set(
                        (draft) => {
                            draft.shutdown = () => {
                                userChangeUnsubscriber();
                                featureFlagUnsubscriber();
                            };
                        },
                        false,
                        "createOnboardingDialogStore(addShutdown)",
                    );
                }, 0);

                return uninitialized;
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "OnboardingDialogStore"},
        ),
    );
}

export function useOnboardingDialogCurrentStep(): OnboardingStep | "uninitialized" {
    return useFluxServices().useOnboardingDialogStore((state) => state.step);
}

export function useOnboardingDialogCurrentFlow(): OnboardingFlow {
    return useFluxServices().useOnboardingDialogStore((state) =>
        "flow" in state && state.flow ? state.flow : OnboardingFlow.enum.standard,
    );
}

export function useOnboardingDialogIsShown(): boolean {
    return useFluxServices().useOnboardingDialogStore((state) => state.isShown);
}

export function useOnboardingDialogOnDismiss(): () => void {
    return useFluxServices().useOnboardingDialogStore((state) =>
        state.allowsDismissal ? state.onDismiss ?? noop : noop,
    );
}

export function useOnboardingDialogOnCompleteSurvey(): SurveyState["callbacks"]["onCompleteSurvey"] {
    return useFluxServices().useOnboardingDialogStore((state) => {
        if (!onboardingDialogStateIsAtStep(state, "survey")) {
            throw new Error("Not at the survey step");
        }

        return state.callbacks.onCompleteSurvey;
    });
}

export function useOnboardingDialogOnUpdateSurvey(): SurveyState["callbacks"]["onUpdateSurvey"] {
    return useFluxServices().useOnboardingDialogStore((state) => {
        if (!onboardingDialogStateIsAtStep(state, "survey")) {
            throw new Error("Not at the survey step");
        }

        return state.callbacks.onUpdateSurvey;
    });
}

export function useOnboardingDialogOnCompleteInvites(): ChooseInvitesState["callbacks"]["onCompleteInvites"] {
    return useFluxServices().useOnboardingDialogStore((state) => {
        if (!onboardingDialogStateIsAtStep(state, OnboardingStep.enum.chooseInvites)) {
            throw new Error("Not at the chooseInvites step");
        }

        return state.callbacks.onCompleteInvites;
    });
}

export function useOnboardingDialogOnPlanChosen(): ChoosePlanState["callbacks"]["onPlanChosen"] {
    return useFluxServices().useOnboardingDialogStore((state) => {
        if (!onboardingDialogStateIsAtStep(state, OnboardingStep.enum.choosePlan)) {
            throw new Error("Not at the choosePlan step");
        }

        return state.callbacks.onPlanChosen;
    });
}
